import React, { Component, createContext, type ReactNode, useContext } from 'react';
import noop from 'lodash/noop';
import type { ProjectType } from '@jira/platform__project-types/src/project-types.tsx';
import { getIsJsmBlankProjectTemplate } from '@atlassian/jira-common-constants/src/project-template-type-keys.tsx';
import { LazyComponent as AsyncProjectBuilderDrawerComponent } from '@atlassian/jira-servicedesk-project-builder/src/async.tsx';
import { useRouterActions, type RouterActionsType } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import type {
	ConsumerData,
	OpenDrawerMethod,
	ProjectCreateSuccessCallbackPayload,
} from './types.tsx';
import { clearResourcesAssociatedWithProjectKey } from './utils.tsx';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
const Context = createContext<ConsumerData>({
	data: {
		shouldRenderDrawer: false,
		isOpen: false,
		isNextGen: false,
		showExperienceSelection: true,
		recommendedTemplate: undefined,
		defaultSelectedTemplateKey: '',
		source: '',
		isTemplatePickerOpenInitially: false,
		isImportProjectOpenInitially: false,
		selectedProjectType: null,
		recommendationSessionId: undefined,
	},
	methods: {
		open: noop,
		close: noop,
		onSuccess: noop,
		onCreateStarted: noop,
		setOnCloseCallback: noop,
		clearOnCloseCallback: noop,
		setOnCreatedCallback: noop,
		clearOnCreatedCallback: noop,
	},
} as ConsumerData);

export type Props = {
	children: ReactNode;
	setLocation: RouterActionsType['push'];
};
type State = {
	isOpen: boolean;
	isOnboarding?: boolean;
	shouldRenderDrawer: boolean;
	isNextGen: boolean;
	showExperienceSelection: boolean;
	recommendedTemplate:
		| undefined
		| {
				templateKey: string;
				isSimplified: boolean;
		  };
	defaultSelectedTemplateKey: string;
	source?: string;
	isTemplatePickerOpenInitially: boolean;
	isImportProjectOpenInitially: boolean;
	selectedProjectType: ProjectType | null;
	recommendationSessionId?: string;
	onCloseCallback: () => void;
	onCreatedCallback: (project: ProjectCreateSuccessCallbackPayload) => void;
};
// eslint-disable-next-line jira/react/no-class-components
export const ProviderInternal = class ProjectCreateDrawerProvider extends Component<Props, State> {
	state = {
		isOpen: false,
		...(fg('jira_project_create_drawer_support_onboarding') && { isOnboarding: false }),
		shouldRenderDrawer: false,
		isNextGen: false,
		showExperienceSelection: true,
		recommendedTemplate: undefined,
		defaultSelectedTemplateKey: '',
		source: '',
		isTemplatePickerOpenInitially: false,
		isImportProjectOpenInitially: false,
		selectedProjectType: null,
		recommendationSessionId: undefined,
		onCloseCallback: noop,
		onCreatedCallback: noop,
	};

	onSuccess = (project?: ProjectCreateSuccessCallbackPayload): void => {
		if (project) {
			this.props.setLocation(project.returnUrl);
			this.close(true);

			this.state.onCreatedCallback(project);
		} else {
			this.close(false);
		}
	};

	// Callback that is called once when the drawer is closed.
	setOnCloseCallback = (callback: () => void) => {
		this.setState({
			onCloseCallback: callback,
		});
	};

	clearOnCloseCallback = () => {
		this.setState({ onCloseCallback: noop });
	};

	// Callback that is called once when a project is successfully created.
	setOnCreatedCallback = (callback: (project: ProjectCreateSuccessCallbackPayload) => void) => {
		this.setState({
			onCreatedCallback: callback,
		});
	};

	clearOnCreatedCallback = () => {
		this.setState({ onCreatedCallback: noop });
	};

	open: OpenDrawerMethod = (attributes = {}) => {
		// @ts-expect-error - TS2345 - Argument of type '{ isNextGen?: boolean | undefined; showExperienceSelection?: boolean | undefined; recommendedTemplate?: { templateKey: string; isSimplified: boolean; } | undefined; defaultSelectedTemplateKey?: string | undefined; ... 4 more ...; shouldRenderDrawer: true; }' is not assignable to parameter of type 'State | ((prevState: Readonly<State>, props: Readonly<Props>) => State | Pick<State, "source" | "isOpen" | "isNextGen" | ... 5 more ... | "isTemplatePickerOpenInitially"> | null) | Pick<...> | null'.
		this.setState({
			isOpen: true,
			shouldRenderDrawer: true,
			...attributes,
		});
	};

	close = (projectCreated?: boolean) => {
		const callOnCloseCallBack = this.state.source === 'navigationNext' || !projectCreated;
		this.setState({
			isOpen: false,
			...(fg('jira_project_create_drawer_support_onboarding') && { isOnboarding: false }),
			isNextGen: false,
			showExperienceSelection: true,
			recommendedTemplate: undefined,
			defaultSelectedTemplateKey: '',
			source: '',
			isTemplatePickerOpenInitially: false,
			isImportProjectOpenInitially: false,
			selectedProjectType: null,
			recommendationSessionId: undefined,
		});

		if (callOnCloseCallBack) {
			// Call the added callback
			this.state.onCloseCallback();
		}
	};

	closeOld = () => {
		this.setState({
			isOpen: false,
			...(fg('jira_project_create_drawer_support_onboarding') && { isOnboarding: false }),
			isNextGen: false,
			showExperienceSelection: true,
			recommendedTemplate: undefined,
			defaultSelectedTemplateKey: '',
			source: '',
			isTemplatePickerOpenInitially: false,
			selectedProjectType: null,
			recommendationSessionId: undefined,
		});
		this.state.onCloseCallback();
	};

	onCreateStarted = (projectKey: string, templateKey: string) => {
		if (getIsJsmBlankProjectTemplate(templateKey)) {
			AsyncProjectBuilderDrawerComponent.preload();
		}
		clearResourcesAssociatedWithProjectKey(projectKey);
	};

	render() {
		const { children } = this.props;
		const {
			isOpen,
			isNextGen,
			source,
			showExperienceSelection,
			shouldRenderDrawer,
			recommendedTemplate,
			defaultSelectedTemplateKey,
			isTemplatePickerOpenInitially,
			isImportProjectOpenInitially,
			selectedProjectType,
			recommendationSessionId,
		} = this.state;

		return (
			<Context.Provider
				value={{
					data: {
						isOpen,
						isNextGen,
						...(fg('jira_project_create_drawer_support_onboarding') && {
							isOnboarding: this.state.isOnboarding,
						}),
						source,
						showExperienceSelection,
						shouldRenderDrawer,
						recommendedTemplate,
						defaultSelectedTemplateKey,
						isTemplatePickerOpenInitially,
						isImportProjectOpenInitially,
						selectedProjectType,
						recommendationSessionId,
					},
					methods: {
						open: this.open,
						close: this.close,
						onSuccess: this.onSuccess,
						onCreateStarted: this.onCreateStarted,
						setOnCloseCallback: this.setOnCloseCallback,
						clearOnCloseCallback: this.clearOnCloseCallback,
						setOnCreatedCallback: this.setOnCreatedCallback,
						clearOnCreatedCallback: this.clearOnCreatedCallback,
					},
				}}
			>
				{children}
			</Context.Provider>
		);
	}
};

export const ProjectCreateDrawerProvider = (props: { children: ReactNode }) => {
	const { push } = useRouterActions();
	return <ProviderInternal {...props} setLocation={push} />;
};

export const { Consumer: ProjectCreateDrawerConsumer } = Context;
Context.displayName = 'ProjectCreateDrawerConsumer';

export const useProjectCreateDrawer = () => {
	const projectCreateDrawer = useContext(Context);
	if (!projectCreateDrawer) {
		throw new Error('useProjectCreateDrawer must be used within a ProjectCreateDrawerProvider');
	}
	return projectCreateDrawer;
};
